import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Cloud, Save, RotateCcw, ExternalLink, Loader2, CheckCircle2, XCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin card for the Google Drive secondary-backup integration.
 *
 * 1. Paste OAuth client_id + client_secret + (optional) folder_id.
 * 2. Click "Connect" — opens Google consent in a new tab. Google redirects
 *    back to /api/admin/drive/callback which stores the refresh token.
 * 3. Toggle Enabled. New uploads from that moment are mirrored to Drive
 *    asynchronously (never blocks the upload itself).
 * 4. "View Backups" lists all files that have been mirrored; per-file
 *    Restore button pulls the bytes back from Drive into primary storage.
 */
export function DriveBackupCard({ api }) {
  const [status, setStatus] = useState({ enabled: false, configured: false, connected: false });
  const [form, setForm] = useState({ client_id: '', client_secret: '', folder_id: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [restoring, setRestoring] = useState({});

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/drive/config');
      setStatus(data);
      setForm((f) => ({ ...f, folder_id: data.folder_id || f.folder_id }));
    } catch (e) {
      // Non-fatal — admin can still configure
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (form.client_id) body.client_id = form.client_id;
      if (form.client_secret) body.client_secret = form.client_secret;
      if (form.folder_id !== undefined) body.folder_id = form.folder_id;
      if (Object.keys(body).length === 0) {
        toast.warning('No new values to save');
        setSaving(false);
        return;
      }
      const { data } = await api.put('/api/admin/drive/config', body);
      setStatus(data);
      setForm((f) => ({ ...f, client_id: '', client_secret: '' }));
      toast.success('Drive credentials saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const toggleEnabled = async (next) => {
    try {
      const { data } = await api.put('/api/admin/drive/config', { enabled: next });
      setStatus(data);
      toast.success(next ? 'Drive backup enabled' : 'Drive backup paused');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle');
    }
  };

  const connect = async () => {
    try {
      const { data } = await api.get('/api/admin/drive/connect');
      window.open(data.authorization_url, '_blank', 'noopener');
      toast.message('Complete the Google consent flow in the new tab, then refresh this page.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start OAuth');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Drive? Existing backups remain but no new files will be mirrored.')) return;
    try {
      await api.post('/api/admin/drive/disconnect');
      await fetchStatus();
      toast.success('Disconnected');
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const openBackups = async () => {
    setShowBackups(true);
    setBackupsLoading(true);
    try {
      const { data } = await api.get('/api/admin/drive/files?limit=100');
      setBackups(data.files || []);
    } catch (e) {
      toast.error('Failed to load backups');
    }
    setBackupsLoading(false);
  };

  const restore = async (fileId) => {
    setRestoring((r) => ({ ...r, [fileId]: true }));
    try {
      await api.post(`/api/admin/drive/restore/${fileId}`);
      toast.success('File restored to primary storage');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
    setRestoring((r) => ({ ...r, [fileId]: false }));
  };

  return (
    <Card className="bg-[#041A3B] border-white/10 mt-4" data-testid="drive-backup-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Cloud className="w-5 h-5 text-[#00E5FF]" /> Google Drive Backup
            </CardTitle>
            <CardDescription className="text-[#A0B4CC]">
              Mirror every uploaded image/file to your Google Drive folder. Used to recover files if primary storage ever loses data.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge label="Configured" ok={status.configured} />
            <StatusBadge label="Connected" ok={status.connected} />
            <StatusBadge label="Enabled" ok={status.enabled} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="text-[#A0B4CC] text-sm py-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Credentials form */}
            <div className="space-y-3">
              <div>
                <Label className="text-[#A0B4CC] text-xs">OAuth Client ID</Label>
                <Input
                  value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  placeholder={status.client_id_preview || 'xxx.apps.googleusercontent.com'}
                  className="bg-[#000B18] border-white/20 text-white"
                  data-testid="drive-client-id"
                />
                {status.client_id_preview && (
                  <p className="text-[#5C7599] text-xs mt-1">Currently set: {status.client_id_preview}</p>
                )}
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">OAuth Client Secret</Label>
                <Input
                  type="password"
                  value={form.client_secret}
                  onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                  placeholder={status.configured ? '••••••••••• (leave blank to keep current)' : 'GOCSPX-…'}
                  className="bg-[#000B18] border-white/20 text-white"
                  data-testid="drive-client-secret"
                />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Target Folder ID <span className="text-[#5C7599]">(optional — leave blank to upload to Drive root)</span></Label>
                <Input
                  value={form.folder_id}
                  onChange={(e) => setForm({ ...form, folder_id: e.target.value })}
                  placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
                  className="bg-[#000B18] border-white/20 text-white"
                  data-testid="drive-folder-id"
                />
                <p className="text-[#5C7599] text-xs mt-1">
                  Find this in the Drive URL: <code>drive.google.com/drive/folders/&lt;folder_id&gt;</code>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={saving}
                  className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                  data-testid="drive-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Credentials</>}
                </Button>
                <Button onClick={connect} disabled={!status.configured} variant="outline"
                  className="border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10"
                  data-testid="drive-connect-btn">
                  <ExternalLink className="w-4 h-4 mr-1.5" /> {status.connected ? 'Re-connect' : 'Connect Drive'}
                </Button>
                {status.connected && (
                  <Button onClick={disconnect} variant="outline" className="border-white/20 text-[#A0B4CC]" data-testid="drive-disconnect-btn">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Disconnect
                  </Button>
                )}
              </div>
              {status.last_error && (
                <p className="text-[#FF1744] text-xs">Last error: {status.last_error}</p>
              )}
            </div>

            {/* Enable + actions */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
              <div className="flex-1 min-w-0 pr-4">
                <Label className="text-white">Backup new uploads to Drive</Label>
                <p className="text-[#5C7599] text-xs mt-0.5">
                  Toggle this off any time to pause backups without disconnecting your Google account.
                  {!status.connected && (
                    <span className="text-[#FFA726]"> (connect Drive first to start mirroring)</span>
                  )}
                </p>
              </div>
              <Switch
                checked={!!status.enabled}
                onCheckedChange={toggleEnabled}
                data-testid="drive-enable-switch"
              />
            </div>

            <Button onClick={openBackups} variant="outline" size="sm"
              className="border-white/20 text-white hover:bg-white/5"
              data-testid="drive-view-backups-btn">
              View Backed-up Files
            </Button>

            {/* Backup list */}
            {showBackups && (
              <div className="rounded-lg border border-white/10 bg-[#000B18] p-3 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white text-sm font-semibold">Drive Backups (latest 100)</p>
                  <Button variant="ghost" size="sm" onClick={() => setShowBackups(false)} className="text-[#A0B4CC] h-7">Close</Button>
                </div>
                {backupsLoading ? (
                  <div className="text-[#A0B4CC] text-sm py-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : backups.length === 0 ? (
                  <p className="text-[#5C7599] text-sm py-2">No files have been backed up yet.</p>
                ) : (
                  <div className="overflow-x-auto mobile-scroll-wrap max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[#000B18] text-[#5C7599] text-left">
                        <tr><th className="p-1.5">Filename</th><th className="p-1.5">Purpose</th><th className="p-1.5">Backed Up</th><th className="p-1.5"></th></tr>
                      </thead>
                      <tbody className="text-white">
                        {backups.map((b) => (
                          <tr key={b.id} className="border-t border-white/5">
                            <td className="p-1.5 truncate max-w-[220px]" title={b.original_filename}>{b.original_filename}</td>
                            <td className="p-1.5 font-mono text-[#00E5FF]">{b.purpose || '-'}</td>
                            <td className="p-1.5 text-[#A0B4CC] whitespace-nowrap">{b.drive_backed_up_at?.slice(0, 16).replace('T', ' ')}</td>
                            <td className="p-1.5 flex gap-1">
                              {b.drive_view_link && (
                                <a href={b.drive_view_link} target="_blank" rel="noopener noreferrer"
                                  className="text-[#00E5FF] hover:underline" title="Open in Drive">
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                              <button onClick={() => restore(b.id)} disabled={!!restoring[b.id]}
                                className="text-[#00E676] hover:underline flex items-center gap-1"
                                title="Restore to primary storage"
                                data-testid={`drive-restore-${b.id}`}>
                                {restoring[b.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <details className="text-[#5C7599] text-xs">
              <summary className="cursor-pointer hover:text-[#A0B4CC]">Setup instructions</summary>
              <ol className="mt-2 ml-4 space-y-1 list-decimal">
                <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#00E5FF]">Google Cloud Console</a> → create/select a project.</li>
                <li>Enable the <b>Google Drive API</b>.</li>
                <li>OAuth consent screen: External → add yourself as a Test User → scope <code>drive.file</code>.</li>
                <li>Create OAuth Client (Web). Authorized redirect URI: <code>{`${window.location.origin}/api/admin/drive/callback`}</code></li>
                <li>Paste the Client ID + Secret above → Save → Connect Drive.</li>
                <li>(Optional) Create a Drive folder, copy its ID from the URL, paste it as Target Folder ID.</li>
              </ol>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ label, ok }) {
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide
      ${ok ? 'bg-[#00E676]/15 text-[#00E676]' : 'bg-white/5 text-[#5C7599]'}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label}
    </div>
  );
}
